import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Utensils, Camera, Flame, Plus, Trash2, X, Calendar,
  TrendingUp, Award, Loader2, ChevronLeft, ChevronRight
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CalorieRing({ current, goal = 2000 }) {
  const percentage = Math.min((current / goal) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full -rotate-90">
        <circle cx="64" cy="64" r="45" stroke="rgba(255,255,255,0.05)" strokeWidth="8" fill="none" />
        <circle
          cx="64" cy="64" r="45"
          stroke={percentage >= 100 ? "#ef4444" : "#00FF88"}
          strokeWidth="8" fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>{current}</span>
        <span className="text-xs text-slate-500">/ {goal} cal</span>
      </div>
    </div>
  );
}

function MealCard({ meal, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl overflow-hidden hover:border-white/10 transition-all"
      data-testid={`meal-card-${meal.meal_id}`}
    >
      <div className="flex gap-4 p-4">
        {meal.file_id && (
          <img
            src={`${BACKEND_URL}/api/vault/files/${meal.file_id}`}
            alt={meal.title}
            className="w-20 h-20 rounded-xl object-cover shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-white truncate">{meal.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {new Date(meal.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </p>
            </div>
            <div className="flex items-center gap-1 bg-[#00FF88]/10 px-2.5 py-1 rounded-full">
              <Flame className="w-3 h-3 text-[#00FF88]" />
              <span className="text-xs font-bold text-[#00FF88]">{meal.total_calories}</span>
            </div>
          </div>

          {meal.foods && meal.foods.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-slate-400 hover:text-white mt-2 transition-colors"
            >
              {expanded ? "Hide details" : `${meal.foods.length} items detected →`}
            </button>
          )}
        </div>
        <button
          onClick={() => onDelete(meal.meal_id)}
          className="p-2 text-slate-600 hover:text-red-400 transition-colors self-start"
          data-testid={`delete-meal-${meal.meal_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {expanded && meal.foods && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 border-t border-white/[0.06]">
              <div className="space-y-2 mt-3">
                {meal.foods.map((food, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">{food.name} <span className="text-slate-600">({food.portion})</span></span>
                    <span className="text-slate-300">{food.calories} cal</span>
                  </div>
                ))}
              </div>
              {meal.ai_notes && (
                <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-white/[0.06]">{meal.ai_notes}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function MealTrackerPage() {
  const [meals, setMeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [uploadForm, setUploadForm] = useState({ title: "", notes: "", file: null });
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mealsRes, statsRes] = await Promise.all([
        api.get(`/meals?date=${selectedDate}`),
        api.get("/meals/stats")
      ]);
      setMeals(mealsRes.data.meals || []);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load meals");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error("Please select a photo");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", uploadForm.file);
      formData.append("title", uploadForm.title || "Meal");
      formData.append("notes", uploadForm.notes);

      const res = await api.post("/meals/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(`Meal logged! ${res.data.total_calories} calories • +${res.data.points_earned} points`);
      setShowUpload(false);
      setUploadForm({ title: "", notes: "", file: null });
      setPreviewUrl(null);
      loadData();
    } catch (err) {
      toast.error("Failed to upload meal");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mealId) => {
    if (!window.confirm("Delete this meal?")) return;
    try {
      await api.delete(`/meals/${mealId}`);
      toast.success("Meal deleted");
      loadData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const todayCalories = meals.reduce((sum, m) => sum + (m.total_calories || 0), 0);

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[900px] mx-auto pb-24" data-testid="meal-tracker-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
              <Utensils className="w-5 h-5 text-[#00FF88]" />
            </div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">Nutrition</p>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Meal Tracker
          </h1>
          <p className="text-sm text-slate-400 mt-1">Track your meals and earn points</p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          data-testid="add-meal-btn"
          className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-medium"
        >
          <Camera className="w-4 h-4 mr-2" /> Log Meal
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4 flex flex-col items-center justify-center">
          <CalorieRing current={stats?.today?.total_calories || 0} />
          <p className="text-xs text-slate-500 mt-2">Today</p>
        </div>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-500">This Week</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
            {stats?.this_week?.total_calories || 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            ~{stats?.this_week?.avg_daily_calories || 0} avg/day
          </p>
        </div>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-slate-500">Streak</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
            {stats?.streak || 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">days tracking</p>
        </div>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-[#00FF88]" />
            <span className="text-xs text-slate-500">Total Meals</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
            {stats?.total_meals || 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">logged</p>
        </div>
      </div>

      {/* Date Navigator */}
      <div className="flex items-center justify-between mb-6 bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-3">
        <button
          onClick={() => changeDate(-1)}
          className="p-2 text-slate-400 hover:text-white transition-colors"
          data-testid="prev-date-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-white">
            {isToday ? "Today" : new Date(selectedDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          </span>
          {!isToday && (
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
              className="text-xs text-[#00FF88] hover:underline ml-2"
            >
              Jump to today
            </button>
          )}
        </div>
        <button
          onClick={() => changeDate(1)}
          disabled={isToday}
          className="p-2 text-slate-400 hover:text-white transition-colors disabled:opacity-30"
          data-testid="next-date-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Daily Summary */}
      {meals.length > 0 && (
        <div className="flex items-center justify-between mb-4 px-1">
          <p className="text-sm text-slate-400">{meals.length} meal{meals.length !== 1 ? "s" : ""}</p>
          <p className="text-sm">
            <span className="text-slate-500">Total:</span>{" "}
            <span className="font-bold text-[#00FF88]">{todayCalories} cal</span>
          </p>
        </div>
      )}

      {/* Meals List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
          <p className="text-sm text-slate-400">Loading meals...</p>
        </div>
      ) : meals.length === 0 ? (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
          <Utensils className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
            No meals logged
          </h3>
          <p className="text-sm text-slate-400 max-w-sm mx-auto">
            {isToday ? "Snap a photo of your meal to track calories and earn points!" : "No meals were logged on this day."}
          </p>
          {isToday && (
            <Button
              onClick={() => setShowUpload(true)}
              className="mt-4 bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Add First Meal
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {meals.map((meal) => (
            <MealCard key={meal.meal_id} meal={meal} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>Log a Meal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl cursor-pointer transition-colors duration-200 overflow-hidden ${
                previewUrl ? "border-[#00FF88]/30" : "border-white/10 hover:border-white/20"
              }`}
              data-testid="meal-upload-dropzone"
            >
              {previewUrl ? (
                <div className="relative">
                  <img src={previewUrl} alt="Preview" className="w-full h-48 object-cover" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewUrl(null);
                      setUploadForm({ ...uploadForm, file: null });
                    }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="p-8 flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-[#00FF88]/10 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-[#00FF88]" />
                  </div>
                  <p className="text-sm text-slate-400">Tap to take or choose a photo</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="meal-file-input"
              />
            </div>

            <Input
              placeholder="Meal name (optional)"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              data-testid="meal-title-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />

            <Input
              placeholder="Notes (optional)"
              value={uploadForm.notes}
              onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
              data-testid="meal-notes-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />

            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3">
              <p className="text-xs text-slate-500">
                Our AI will analyze your meal photo to identify foods and estimate calories automatically.
              </p>
            </div>

            <Button
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file}
              data-testid="meal-submit-btn"
              className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full h-12 font-medium disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...
                </>
              ) : (
                <>
                  <Utensils className="w-4 h-4 mr-2" /> Log Meal (+20 pts)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
